import { inject } from '@angular/core';

import { AssetService } from '@karve.it/features';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store, createSelector } from '@ngrx/store';
import { MutationOptionsAlone, QueryOptionsAlone } from 'apollo-angular/types';
import {
  BulkEditCalendarEventMutationVariables,
  BulkEditCalendarEventOnScheduleGQL,
  CalendarEvent_LocationFragment,
  FindGQL,
  FindQuery,
  FindQueryVariables,
  LockWindowGQL,
  LockWindowMutationVariables,
  SetAssets,
  UpdateJobGQL,
  UpdateJobMutation,
  UpdateJobMutationVariables,
} from 'graphql.generated';
import { isEqual } from 'lodash';
import { ConfirmationService } from 'primeng/api';
import {
  catchError,
  filter,
  map,
  of,
  switchMap,
  tap,
  debounceTime,
  exhaustMap,
  mergeMap,
  concatMap,
} from 'rxjs';
import { generateMutationContext } from 'src/app/core/auth/session';
import { PlusAuthenticationService } from 'src/app/core/public-api';
import { eventTypeInfoMapV2, JobRole } from 'src/app/global.constants';
import { JobToolActions } from 'src/app/job/job-tool.actions';
import { selectJob, selectJobFranchiseId, selectJobId, selectJobTimezone } from 'src/app/job/job-tool.reducer';
import { strToTitleCase } from 'src/app/js';
import { YemboHelperService } from 'src/app/services/yembo-helper.service';

import { selectEventTypes, selectQubeSheetsEnabled } from 'src/app/state/branding.store';

import { AssetsInput } from '../../../../interfaces/assets';

import { getResourceChanges } from '../../../job-helpers';

import { BookEventDialogActions } from './timeline-book-event.actions';
import { timelineBookEventFeature } from './timeline-book-event.feature';
import { reloadWindowsTriggers } from './timeline-book-event.reducer';
import {
  selectEventItineraryLocations,
  selectEventToBook,
  selectPlaceholderEvent,
  selectTotalTime
} from './timeline-book-event.selectors';

export const findAvailability = createEffect(
  (
    actions$ = inject(Actions),
    findGQL = inject(FindGQL),
    store = inject(Store),
  ) => {
    return actions$.pipe(
      ofType(...reloadWindowsTriggers),
      debounceTime(200),
      concatLatestFrom(() => [
        store.select(timelineBookEventFeature.selectBookEventDialog),
        store.select(selectEventToBook),
        store.select(selectJob),
        store.select(selectTotalTime),
        store.select(selectJobTimezone)
      ]),
      filter(([ _action, { date, restrictionsDisabled }, eventToBook ]) => Boolean(date) && Boolean(eventToBook) && !restrictionsDisabled),
      tap(() => store.dispatch(BookEventDialogActions.findAvailabilityRequested())),
      map(([ _action, { date, restrictionsDisabled }, eventToBook, job, totalTime, timezone ]): {
        vars: FindQueryVariables;
        opts: QueryOptionsAlone<FindQueryVariables, FindQuery>;
        timezone: string;
      } => {

        return {
          vars: {
            timeWindow: {
              startDate: date,
              endDate: date,
              minWindowLength: totalTime,
            },
            minNumAssets: 1,
            assetsFilter: {
              types: eventTypeInfoMapV2[eventToBook.type]?.assetTypes || [],
            },
            overrideAvailabilities: restrictionsDisabled,
            overrideUnavailabilities: restrictionsDisabled,
            zoneInput: job.zone.id,
            excludedEventIds: [ eventToBook.id ],
          },
          opts: { fetchPolicy: 'network-only' },
          timezone,
        };
      }),
      switchMap(({ vars, opts, timezone }) => findGQL.fetch(vars, opts).pipe(
        map((response) => BookEventDialogActions.findAvailabilitySuccess({ windows: response.data.find.windows, timezone })),
        catchError((error) => of(BookEventDialogActions.findAvailabilityError({ error }))),
      )),
    );
  },
  { functional: true, dispatch: true }
);

export const loadSelectedTimeWithTimezone = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
) => {
    return actions$
      .pipe(
        ofType(BookEventDialogActions.timeSelected),
        concatLatestFrom(() => store.select(selectJobTimezone)),
        map(([ { time }, timezone ]) => BookEventDialogActions.timeWithTimezoneSelected({ time, timezone })),
      );
  }, { functional: true, dispatch: true });

export const confirmBookVirtualEstimateWithBothIntegrationsEnabled = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  yemboHelper = inject(YemboHelperService),
  confirmationService = inject(ConfirmationService),
) => {
    return actions$.pipe(
      ofType(BookEventDialogActions.bookButtonClicked),
      filter(() => yemboHelper.yemboEnabled),
      exhaustMap((action) => of(action).pipe(
        concatLatestFrom(() => [
          store.select(selectPlaceholderEvent),
          store.select(selectQubeSheetsEnabled),
        ]),
        filter(([ _action, event, qubeSheetsEnabled ]) => qubeSheetsEnabled && event.type === 'virtualEstimate'),
        map(() => {

          confirmationService.confirm({
            header: 'Integrations Conflict',
            message: 'Yembo and Qube Sheets integrations can’t be enabled at once and Smart Consult / Virtual Call will not be scheduled.',
            accept: () => store.dispatch(BookEventDialogActions.bookVirtualEstimateWithoutVideoCallConfirmationButtonClicked()),
            reject: () => store.dispatch(BookEventDialogActions.bookVirtualEstimateWithoutVideoCallCancelButtonClicked()),
            acceptLabel: 'Continue',
            rejectLabel: 'Cancel',
          });

          return BookEventDialogActions.integrationsConflictConfirmationShown();
        }),
        catchError((error) => of(BookEventDialogActions.integrationsConflictConfirmationError({ error }))),
      )),
    );

}, { functional: true, dispatch: true });

export const confirmBookVirtualEstimateWithQubeSheetsOnly = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  yemboHelper = inject(YemboHelperService),
) => {
    return actions$.pipe(
      ofType(BookEventDialogActions.bookButtonClicked),
      filter(() => !yemboHelper.yemboEnabled),
      concatLatestFrom(() => [
        store.select(selectPlaceholderEvent),
        store.select(selectQubeSheetsEnabled),
      ]),
      filter(([ _action, event, qubeSheetsEnabled ]) => qubeSheetsEnabled && event.type === 'virtualEstimate'),
      map(() => BookEventDialogActions.virtualEstimateReadyForBooking()),
    );

}, { functional: true, dispatch: true });

export const confirmBookSmartConsultForDefaultAdminEmail = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  assetService = inject(AssetService),
  yemboHelper = inject(YemboHelperService),
  confirmationService = inject(ConfirmationService),
) => {
    return actions$.pipe(
      ofType(BookEventDialogActions.bookButtonClicked),
      filter(() => yemboHelper.yemboEnabled),
      concatLatestFrom(() => [
        store.select(timelineBookEventFeature.selectBookEventDialog),
        store.select(selectPlaceholderEvent),
        store.select(selectQubeSheetsEnabled),
      ]),
      filter(([ _action, _bookEventDialogData, event, qubeSheetsEnabled ]) => !qubeSheetsEnabled && event.type === 'virtualEstimate'),
      map(([ _action, { assetIds }]): AssetsInput => ({
        filter: {
          ids: assetIds,
        }
      })),
      exhaustMap((vars) => assetService.listAssets(vars).pipe(
        map((res) => res.data.assets.assets),
        map((assets) => assets.some((a) => a.metadata?.yemboEmail)),
        tap((hasAssetWithYemboEmailConfigured) => {
          if (hasAssetWithYemboEmailConfigured) {
            store.dispatch(BookEventDialogActions.virtualEstimateReadyForBooking())
          } else {
            confirmationService.confirm({
              header: 'No Yembo Email',
              message: 'This asset does not have a Yembo email configured. Smart Consult will be booked for the default admin email.',
                accept: () => store.dispatch(BookEventDialogActions.bookSmartConsultForDefaultAdminEmailConfirmationButtonClicked()),
                reject: () => store.dispatch(BookEventDialogActions.bookSmartConsultForDefaultAdminEmailCancelButtonClicked()),
              acceptLabel: 'Continue',
              rejectLabel: 'Cancel',
            });
          }
        }),
      )),
    );

}, { functional: true, dispatch: false });

export const bookEvent = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  lockWindowGQL = inject(LockWindowGQL),
) => {
    return actions$.pipe(
      ofType(
        BookEventDialogActions.bookButtonClicked,
        BookEventDialogActions.virtualEstimateReadyForBooking,
        BookEventDialogActions.bookSmartConsultForDefaultAdminEmailConfirmationButtonClicked,
        BookEventDialogActions.bookVirtualEstimateWithoutVideoCallConfirmationButtonClicked,
      ),
      exhaustMap((action) => of(action).pipe(
        concatLatestFrom(() => [
          store.select(timelineBookEventFeature.selectBookEventDialog),
          store.select(selectPlaceholderEvent),
          store.select(selectJobId),
          store.select(selectEventItineraryLocations)
        ]),
        filter(([ action, _dialogData, eventToBook ]) => {

          const status = eventToBook?.status;
          if (!status || ![ 'required', 'pending' ].includes(status)) {
            return false;
          }

          // Virtual estimates require confirmation
          if (eventToBook.type === 'virtualEstimate') {
            return action.type === BookEventDialogActions.bookSmartConsultForDefaultAdminEmailConfirmationButtonClicked.type ||
              action.type === BookEventDialogActions.virtualEstimateReadyForBooking.type ||
              action.type === BookEventDialogActions.bookVirtualEstimateWithoutVideoCallConfirmationButtonClicked.type;
          }

          return true;
        }),
        map(([ action, { assetIds, restrictionsDisabled }, event, jobId, locations ]): {
          vars: LockWindowMutationVariables;
          jobId: string;
        } => {

          const locationsInput: LockWindowMutationVariables['locations'] = locations
            .filter((l) => l.location?.id)
            .map((l) => ({
              locationId: l.location.id,
              estimatedTimeAtLocation: l.estimatedTimeAtLocation,
              order: l.order,
              type: l.locationType,
            }));

          return {
            vars: {
              type: event.type,
              assetIds,
              title: event.title || strToTitleCase(event.type),
              start: event.start,
              end: event.end,
              jobId,
              updateEventSequentialOrder: true,
              eventId: event.id,
              overrideAvailabilities: restrictionsDisabled,
              overrideUnavailabilities: restrictionsDisabled,
              attributes: [ event.type ],
              restrictionsEnabled: !restrictionsDisabled,
              locations: locationsInput,
              skipVideoCallScheduling: action.type ===
                BookEventDialogActions.bookVirtualEstimateWithoutVideoCallConfirmationButtonClicked.type,
            },
            jobId,
          }
        }),
        mergeMap(({ vars, jobId }) => lockWindowGQL.mutate(vars).pipe(
          map((res) => BookEventDialogActions.bookEventSuccess({
            updatedEvent: res.data.lockWindow,
            jobId,
          })),
        )),
        catchError((error) => of(BookEventDialogActions.bookEventError({ error }))),
      )),
    );
}, { functional: true, dispatch: true });

export const autoAssignRolesOnBooking = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  updateJobGQL = inject(UpdateJobGQL),
  plusAuth = inject(PlusAuthenticationService),
) => {
    return actions$.pipe(
      ofType(
        BookEventDialogActions.bookEventSuccess,
        BookEventDialogActions.rescheduleEventSuccess,
      ),
      concatLatestFrom(({ updatedEvent }) => store.select(createSelector(
        selectEventTypes,
        selectJob,
        selectJobFranchiseId,
        (eventTypes, job, jobFranchiseId) => {

          const rolesToAssign = eventTypes[updatedEvent.type]?.autoAssignRoles ?? [];

          return {
            rolesToAssign: rolesToAssign.filter((role) => !job?.users?.some((u) => u.role === role)),
            jobId: job?.id,
            jobFranchiseId,
          };
        }
      ))),
      filter(([ _action, { rolesToAssign, jobId } ]) => rolesToAssign.length > 0 && Boolean(jobId)),
      map(([ _action, { rolesToAssign, jobId, jobFranchiseId }]): {
        vars: UpdateJobMutationVariables;
        opts: MutationOptionsAlone<UpdateJobMutation, UpdateJobMutationVariables>;
        rolesToAssign: JobRole[];
      } => ({
        vars: {
          updateJobs: {
            jobId,
            addUsers: rolesToAssign.map((role) => ({ role, userId: plusAuth.user.id })),
          },
        },
        opts: {
          context: generateMutationContext({ zone: jobFranchiseId }),
        },
        rolesToAssign,
      })),
      concatMap(({ vars, opts, rolesToAssign }) => updateJobGQL.mutate(vars, opts).pipe(
        tap((res) => {

          const updateResult = res.data?.updateJobs;
          const updatedJob = updateResult?.jobs?.[0];

          if (!updateResult?.isSuccess || !updatedJob) {
            throw new Error('Auto-assign roles mutation did not return an updated job')
          }

        }),
        map((res) => JobToolActions.autoAssignRolesSuccess({
          updatedJob: res.data.updateJobs.jobs[0],
          customSuccessMessage: `Automatically assigned roles to user: ${rolesToAssign.join(', ')}`,
        })),
        catchError((error) => of(JobToolActions.autoAssignRolesError({ error }))),
      )),
    );
}, { functional: true, dispatch: true });

export const rescheduleEvent = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  bulkEditCalendarEventGQL = inject(BulkEditCalendarEventOnScheduleGQL),
) => {
    return actions$.pipe(
      ofType(BookEventDialogActions.bookButtonClicked),
      exhaustMap((action) => of(action).pipe(
        concatLatestFrom(() => [
          store.select(timelineBookEventFeature.selectBookEventDialog),
          store.select(selectEventToBook),
          store.select(selectPlaceholderEvent),
          store.select(selectJobId),
          store.select(selectEventItineraryLocations)
        ]),
        filter(([ _action, _dialogData, eventToBook ]) => {
          return !['required', 'pending'].includes(eventToBook.status);
        }),
        map(([ _action, { assetIds, restrictionsDisabled }, originalEvent, eventEdit, jobId, locations ]): {
          vars: BulkEditCalendarEventMutationVariables;
          jobId: string;
        } => {

          const { added: addLocations, removed: removeLocations, edited: editLocations } =
            getResourceChanges<CalendarEvent_LocationFragment>({
              original: originalEvent.locations || [],
              modified: locations as unknown as CalendarEvent_LocationFragment[],
              keyExtractor: (item) => `${item.type}-${item.id}`,
              compareFn: (original, modified) =>
                original.order === modified.order &&
                original.estimatedTimeAtLocation === modified.estimatedTimeAtLocation &&
                original.travelTimeToNextLocationOffset === modified.travelTimeToNextLocationOffset,
              detectEdited: true
          });

          const setAssets: SetAssets = {};

          const currentAssetIds = originalEvent?.assets?.map((a) => a.id) || [];

          if (!isEqual(assetIds, currentAssetIds)) {
            setAssets.removeAssets = currentAssetIds.filter((id) => !(assetIds.includes(id)));
            setAssets.addAssets = assetIds;
          }

          return {
            jobId,
            vars: {
              restrictionsEnabled: !restrictionsDisabled,
              edits: [
                {
                  id: eventEdit.id,
                  edit: {
                    start: eventEdit.start,
                    end: eventEdit.end,
                    setAssets,
                    setLocations: {
                      addLocations: addLocations.map((location) => ({
                        estimatedTimeAtLocation: location.estimatedTimeAtLocation,
                        travelTimeToNextLocationOffset: location.travelTimeToNextLocationOffset,
                        ignoreInDuration: false,
                        locationId: location?.location?.id,
                        order: location.order,
                        type: location.type,
                      })),
                      editLocations: editLocations.map((location) => ({
                        id: location.id,
                        estimatedTimeAtLocation: location.estimatedTimeAtLocation,
                        travelTimeToNextLocationOffset: location.travelTimeToNextLocationOffset,
                        ignoreInDuration: false,
                        order: location.order,
                        type: location.type,
                      })),
                      removeLocations: removeLocations.map((rl) => rl.id),
                    },
                  },
                }
              ],
            },
          }
        }),
        exhaustMap(({ vars, jobId }) => bulkEditCalendarEventGQL.mutate(vars).pipe(
          map((res) => BookEventDialogActions.rescheduleEventSuccess({
            updatedEvent: res.data.bulkEditCalendarEvent.events[0],
            jobId,
          })),
        )),
        catchError((error) => of(BookEventDialogActions.rescheduleEventError({ error }))),
      )),
    );
}, { functional: true, dispatch: true });
